import { Pagination_Args } from '../chunks';

/** A namespace that exports type safe wrappers of the standard library */
export namespace std {
    export namespace String {
        /** Copied from stack overflow
         *
         * @link https://stackoverflow.com/questions/60269936/typescript-convert-generic-object-from-snake-to-camel-case */
        export type toSnakeCase<S extends string> = S extends `${infer T}${infer U}`
            ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${toSnakeCase<U>}`
            : S;

        /** Converts a given camelCase string to snake_case */
        export function toSnakeCase<T extends string>(str: T) {
            return str
                .replaceAll(' ', '')
                .replace(/[A-Z]| /g, (str: string) => `_${str.toLowerCase()}`) as toSnakeCase<T>;
        }

        /** Copied from stack overflow
         *
         * @link https://stackoverflow.com/questions/57807009/typescript-generic-to-turn-underscore-object-to-camel-case */
        export type toCamelCase<
            T extends string,
            C extends string = '',
        > = T extends `${infer Pre}_${infer Rest}`
            ? toCamelCase<Capitalize<Rest>, `${C}${Pre}`>
            : `${C}${T}`;

        /** Converts a given snake_case string to camelCase */
        export function toCamelCase<T extends string>(str: T) {
            const parts = str.split(/_| /);
            let camelCased = parts[0]!;

            for (let index = 1; index < parts.length; index++) {
                const p = parts[index]!;

                if (p.length === 0) continue;

                camelCased += `${p[0]!.toUpperCase()}${p.slice(1)}`;
            }

            return camelCased as toCamelCase<T>;
        }

        export function toUpperCase<T extends string>(str: T): Uppercase<T> {
            return str.toUpperCase() as Uppercase<T>;
        }
        export function toLowerCase<T extends string>(str: T): Lowercase<T> {
            return str.toLowerCase() as Lowercase<T>;
        }

        /**
         * Removes all non-printable characters from the given string.
         * This is usefull when importing data and storing it as JSON. If you don't sanitize the data containing these characters,
         * JSON parse will fail.
         */
        export function removeNonPrintableCharacters(str: string): string {
            // eslint-disable-next-line no-control-regex
            return str.replaceAll(/[\u000B\u000C\u001f]/g, '');
        }
    }

    export namespace Object {
        export function keys<T extends Record<string, any>>(object: T) {
            return globalThis.Object.keys(object) as Array<keyof T>;
        }

        /** Works just like the native `Object.entries()` but returns correctly typed tuples
         *
         * ### Example
         * ```typescript
         * const obj = { hello: 'world', coords: [0, 0] as [x: number, y: number] }
         * const entries = std.Object.entries(obj)
         * //    ^ Array<[key: 'hello', value: string] | [key: 'coords', value: [x: number, y: number] ]>
         * ``` */
        export function entries<T extends Record<string, any>>(object: T) {
            return globalThis.Object.entries(object) as Array<
                {
                    [K in keyof T]-?: [key: K, value: T[K]];
                }[keyof T]
            >;
        }

        /** Works just like the `Omit` TypeScript type but with actual JavaScript values */
        export function omit<T extends Record<string, any>, K extends keyof T>(
            object: T,
            keys: K[],
        ) {
            const pickedObj = { ...object } as Partial<T>;

            for (let index = 0; index < keys.length; index++) {
                delete pickedObj[keys[index]!];
            }

            return pickedObj as Omit<T, K>;
        }

        /** Works just like the `Pick` TypeScript type but with actual JavaScript values */
        export function pick<T extends Record<string, any>, K extends keyof T>(
            object: T,
            keys: K[],
        ): Pick<T, K> {
            const pickedObj = {} as Partial<T>;

            for (let index = 0; index < keys.length; index++) {
                const k = keys[index]!;
                pickedObj[k] = object[k];
            }

            return pickedObj as Pick<T, K>;
        }

        function renameKeys<
            T extends Record<string, any>,
            C extends (key: Extract<keyof T, string>) => string,
        >(
            object: T,
            callback: C,
        ): {
            [K in keyof T as ReturnType<C>]: T[K];
        } {
            const parsedObj = {} as any;
            const keys = globalThis.Object.keys(object) as unknown as any[];

            for (let index = 0; index < keys.length; index++) {
                const k = keys[index]!;

                parsedObj[callback(k)] = object[k];
            }

            return parsedObj;
        }

        export type toSnakeCase<T extends Record<string, any>> = {
            [K in keyof T as std.String.toSnakeCase<K & string>]: T[K];
        };

        /** Converts all camelCase keys in the object to snake_case */
        export function toSnakeCase<T extends Record<string, any>>(object: T) {
            return renameKeys(object, key =>
                key === key.toLowerCase() ? key : String.toSnakeCase(key),
            ) as unknown as {
                [K in keyof T as String.toSnakeCase<K & string>]: T[K];
            };
        }

        export type toCamelCase<T extends Record<string, any>> = {
            [K in keyof T as std.String.toCamelCase<K & string>]: T[K];
        };

        /** Converts all snake_case keys in the object to camelCase */
        export function toCamelCase<T extends Record<string, any>>(object: T) {
            return renameKeys(object, String.toCamelCase) as unknown as {
                [K in keyof T as String.toCamelCase<K & string>]: T[K];
            };
        }

        /** Removes all nullish (`null` | `undefined`) keys from the given `record` **in place**. */
        export function removeNullish<T extends Record<string, unknown>>(record: T) {
            // eslint is being stupid here
            for (const key in std.Object.keys(record)) {
                const value = record[key];
                if (value === null || value === undefined) {
                    delete record[key];
                }
            }

            return record as {
                [
                    K in keyof T as Exclude<T[K], undefined | null> extends never ? never : K
                ]: NonNullable<T[K]>;
            };
        }

        export function fromEntries<K extends PropertyKey, V>(entries: Iterable<readonly [K, V]>) {
            return globalThis.Object.fromEntries(entries) as Record<K, V>;
        }

        export function groupByKey<T extends Record<string, unknown>, K extends keyof T>(
            items: T[],
            key: K,
        ) {
            type GroupKey = Extract<T[K], PropertyKey>;

            const result = {} as Partial<Record<GroupKey, T[]>>;
            for (let i = 0; i < items.length; i++) {
                const value = items[i]!;
                const keyValue = value[key] as GroupKey;

                if (keyValue in result) {
                    result[keyValue]!.push(value);
                } else {
                    result[keyValue] = [value];
                }
            }

            return result;
        }

        export function groupByKeyUnique<T extends Record<string, unknown>, K extends keyof T>(
            items: T[],
            key: K,
        ) {
            type GroupKey = Extract<T[K], PropertyKey>;

            const result = {} as Partial<Record<GroupKey, T>>;
            for (let i = 0; i < items.length; i++) {
                const value = items[i]!;
                const keyValue = value[key] as GroupKey;

                if (keyValue in result) {
                    throw new Error(
                        `Every items[i][key] value should be unique. Duplicate key: ${JSON.stringify(
                            keyValue,
                        )}`,
                    );
                }

                result[keyValue] = value;
            }

            return result;
        }

        export function groupBy<K extends PropertyKey, T>(
            values: Array<T>,
            keyFinder: (item: T, index: number) => K,
        ) {
            // using reduce to aggregate values
            return values.reduce(
                (acc, v, i) => {
                    // depending upon the type of keyFinder
                    // if it is function, pass the value to it
                    // if it is a property, access the property
                    const key = keyFinder(v, i);

                    // aggregate values based on the keys
                    if (!acc[key]) {
                        acc[key] = [v];
                    } else {
                        acc[key].push(v);
                    }

                    return acc;
                },
                {} as Partial<Record<K, T[]>>,
            );
        }

        export function requireNonNullish<T extends Record<string, unknown>>(
            value: T,
        ):
            | { ok: false; missingKeys: Array<keyof T> }
            | { ok: true; data: { [K in keyof T]-?: Exclude<T[K], undefined | null> } } {
            const entries = Object.entries(value);

            const failedKeys: Array<keyof T> = [];

            for (const [key, value] of entries) {
                if (value === undefined || value === null) {
                    failedKeys.push(key);
                }
            }

            if (failedKeys.length !== 0) {
                return {
                    ok: false,
                    missingKeys: failedKeys,
                } as const;
            }

            return {
                ok: true,
                data: value as any,
            } as const;
        }

        export function requireKeys<T extends Record<string, unknown>, K extends keyof T>(
            object: T,
            keys: K[],
        ): { ok: false; missingKeys: K[] } | { ok: true; data: Omit<T, K> & Required<Pick<T, K>> } {
            const failedKeys: Array<K> = [];

            for (const k of keys) {
                if (object[k] === undefined) {
                    failedKeys.push(k);
                }
            }

            if (failedKeys.length !== 0) {
                return {
                    ok: false,
                    missingKeys: failedKeys,
                } as const;
            }

            return {
                ok: true,
                data: object as any,
            } as const;
        }

        export function mapValues<T extends Record<string, unknown>, R>(
            object: T,
            callbackFn: (value: T[keyof T]) => R,
        ): Record<keyof T, R> {
            return std.Object.fromEntries(
                std.Object.entries(object).map(([key, value]) => [key, callbackFn(value)] as const),
            );
        }

        export function isRecord(value: unknown): value is Record<string, unknown> {
            return (
                typeof value === 'object' &&
                value !== null &&
                globalThis.Array.isArray(value) === false
            );
        }
    }

    /** A namespace containing useful filters to be passed to `Array.filter()` or in if-statements. */
    export namespace Filters {
        /** Returns `true` if `x !== null | undefined`
         *
         * ### Example
         * ```typescript
         * const myArr: Array<string | null | undefined> = []
         * const filteredArr = myArr.filter(Filters.nonnull)
         * //    ^ Array<string>
         * ``` */
        export const nonnull = <T>(x: T): x is Exclude<T, undefined | null> => x != null;

        export const ok = <T>(x: T): x is Exclude<T, undefined | null | false> => !!x;

        /** A higher order function that creates a filter function based on the passed `union`
         * The filter function returns `true` if `union.includes(x)`
         *
         * ### Example
         * ```typescript
         * const myArr = ['foo', 'bar', 'baz', 'hello', 'world']
         * //    ^ Array<string>
         *
         * const filteredArr = myArr.filter(Filters.inUnion(['hello', 'world']))
         * //    ^ Array<'hello' | 'world'>
         * ``` */
        export const inUnion =
            <T>(union: readonly T[]) =>
            (x: any): x is T =>
                union.includes(x);
    }
    export namespace Function {
        /** Wraps the given function in a try-catch and returns the result in a union. */
        export function safe<T extends (...args: any[]) => unknown>(func: T) {
            return (
                ...args: Parameters<T>
            ):
                | {
                      ok: true;
                      value: ReturnType<T>;
                  }
                | {
                      ok: false;
                      error: unknown;
                  } => {
                try {
                    const value = func(...args) as ReturnType<T>;

                    return {
                        ok: true,
                        value,
                    } as const;
                } catch (error) {
                    return {
                        ok: false,
                        error,
                    } as const;
                }
            };
        }
    }

    export namespace Array {
        /** @param [mode='inclusive'] */
        export function range(
            min: number,
            max: number,
            mode: 'inclusive' | 'exclusive' = 'inclusive',
        ): number[] {
            return new globalThis.Array((mode === 'inclusive' ? 1 : 0) + max - min)
                .fill(undefined)
                .map((_, i) => i + min);
        }

        export function add<T>(array: T[], value: NoInfer<T>): T[] {
            return [...array, value];
        }

        export function remove<T>(array: T[], value: NoInfer<T>): T[] {
            return array.filter(v => v !== value);
        }

        /**
         * Toggles the given value in the given array
         * !Be carefull with using object as values, as it will be compared by reference!
         * @param value
         * @param array
         * @returns
         */
        export function toggle<T>(array: T[], value: T): T[] {
            return array.includes(value) ? remove(array, value) : add(array, value);
        }

        export function has<T>(array: readonly T[], value: unknown): value is T {
            return array.includes(value as any);
        }

        export function intersection<T>(...arrays: Array<T[]>): T[] {
            const countMap = new globalThis.Map<T, number>();

            for (const arr of arrays) {
                for (const entry of arr) {
                    countMap.set(entry, (countMap.get(entry) ?? 0) + 1);
                }
            }

            const result: T[] = [];
            for (const [entry, count] of countMap.entries()) {
                if (count === arrays.length) result.push(entry);
            }

            return result;
        }

        export function chunk<T>(array: T[], chunkSize: number): T[][] {
            const chunks: T[][] = [];

            const chunkCount = Math.ceil(array.length / chunkSize);

            for (let index = 0; index < chunkCount; index++) {
                chunks.push(array.slice(index * chunkSize, (index + 1) * chunkSize));
            }

            return chunks;
        }

        export function paginate<T>(array: T[], pagination: Pagination_Args) {
            const offset = Pagination_Args.getOffset(pagination);
            const size = Pagination_Args.getSize(pagination);

            return {
                /** Total amount of pages */
                totalPages: Math.ceil(array.length / size),
                /** Index of the current page */
                pageIdx: Pagination_Args.getPageIdx(pagination),
                /** Size of the page */
                size,
                /** 0-based offset into the whole `array` */
                offset,
                /** The array slice containing the data of the page */
                slice: array.slice(offset, offset + size),
            } as const;
        }

        /** Works like `Array.map()` and `Array.filter()` in one function call with the added bonus of type inference.
         *
         * Maps over the given `array`. If the mapper `callbackFn` returns `null` the value is filtered out from the resulting array.
         *
         * NOTE: don't abuse this function if it's not needed
         */
        export function filterMap<T, R>(
            array: T[],
            callbackFn: (value: NoInfer<T>, index: number, array: NoInfer<T>[]) => R | null,
        ): R[] {
            const results: R[] = [];

            const { length } = array; // Caching this value is faster
            for (let i = 0; i < length; i++) {
                const r = callbackFn(array[i]!, i, array);

                if (r !== null) results.push(r);
            }

            return results;
        }

        /** Get a random element from the given `array` */
        export function randomValue<T>(array: T[]): T {
            if (array.length === 0) {
                throw new Error('Array cannot be empty');
            }
            const randomIndex = Math.floor(Math.random() * array.length);
            return array[randomIndex]!;
        }

        export function* iterPages<T>(array: T[], windowSize: number) {
            if (windowSize <= 0) {
                throw new Error(`Window "${windowSize}" size must be greater than 0`);
            }

            const totalPages = Math.ceil(array.length / windowSize);
            for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
                yield std.Array.paginate(array, { pageIdx, size: windowSize });
            }
        }
    }

    export namespace Map {
        /**
         * Sums all numeric values in a Map.
         *
         * @example
         * ```typescript
         * const counts = new Map([['a', 10], ['b', 20], ['c', 30]]);
         * std.Map.sumValues(counts) // 60
         * ```
         */
        export function sumValues(map: globalThis.Map<unknown, number>): number {
            let total = 0;
            for (const value of map.values()) {
                total += value;
            }
            return total;
        }

        /**
         * Aggregates array items into a Map, accumulating values for duplicate keys.
         * Useful for counting occurrences or summing values by key.
         *
         * @param items - Array of items to aggregate
         * @param keyFn - Function to extract the key from each item (return null/undefined to skip)
         * @param valueFn - Function to compute the new value given the item and current accumulated value
         *
         * @example
         * ```typescript
         * // Count occurrences by category
         * const items = [{ cat: 'A' }, { cat: 'B' }, { cat: 'A' }];
         * std.Map.aggregate(items, item => item.cat, (_, count) => count + 1)
         * // Map { 'A' => 2, 'B' => 1 }
         *
         * // Sum values by key
         * const sales = [{ region: 'EU', amount: 100 }, { region: 'US', amount: 200 }, { region: 'EU', amount: 50 }];
         * std.Map.aggregate(sales, s => s.region, (s, total) => total + s.amount)
         * // Map { 'EU' => 150, 'US' => 200 }
         * ```
         */
        export function aggregate<T, K extends PropertyKey>(
            items: T[],
            keyFn: (item: T) => K | null | undefined,
            valueFn: (item: T, current: number) => number,
        ): globalThis.Map<K, number> {
            const result = new globalThis.Map<K, number>();
            for (const item of items) {
                const key = keyFn(item);
                if (key == null) continue;
                result.set(key, valueFn(item, result.get(key) ?? 0));
            }
            return result;
        }
    }

    export namespace Number {
        /** Like `Number.toString()` but with better defaults, taking locale into account */
        export function toString(number: number, fractionDigits?: number) {
            const num = fractionDigits === undefined
                ? number
                : std.Number.toFixed(number, fractionDigits); // prettier-ignore

            return num.toLocaleString('nl-BE');
        }

        export function toFixed(number: number, fractionDigits: number): number {
            const order = 10 ** fractionDigits;
            return Math.round(number * order) / order;
        }

        function safeParse(
            string: string | null,
            parseFn: (numStr: string) => number,
        ): number | null {
            if (typeof string !== 'string') return null;

            const number = parseFn(string);

            return globalThis.Number.isNaN(number) ? null : number;
        }

        export function safeParseInt(string: string | null): number | null {
            return safeParse(string, parseInt);
        }

        export function safeParseFloat(string: string | null): number | null {
            return safeParse(string, parseFloat);
        }

        export function inRange(number: number, min?: number | null, max?: number | null): boolean {
            return (
                (typeof min !== 'number' || number >= min) &&
                (typeof max !== 'number' || number <= max)
            );
        }

        export function clamp(number: number, min: number | null, max: number | null): number {
            if (min !== null && number < min) return min;
            if (max !== null && number > max) return max;
            return number;
        }

        const _isArray: (value: unknown) => value is unknown[] = globalThis.Array.isArray;

        /** Converts an unknown value to an array of finite integers.
         * Handles arrays, single numbers, strings, and mixed types.
         * Returns an empty array if no valid numbers are found.
         *
         * @example
         * ```typescript
         * toNumericArray([1, '2', 3]) // [1, 2, 3]
         * toNumericArray('42') // [42]
         * toNumericArray(42) // [42]
         * toNumericArray(['invalid', '123']) // [123]
         * toNumericArray(null) // []
         * ```
         */
        export function toNumericArray(value: unknown): number[] {
            if (!_isArray(value)) {
                if (typeof value === 'number') {
                    return globalThis.Number.isFinite(value) ? [value] : [];
                }
                if (typeof value === 'string') {
                    const parsed = safeParseInt(value);
                    return parsed !== null ? [parsed] : [];
                }
                return [];
            }

            const result: number[] = [];
            for (const item of value) {
                if (typeof item === 'number' && globalThis.Number.isFinite(item)) {
                    result.push(item);
                } else if (typeof item === 'string') {
                    const parsed = safeParseInt(item);
                    if (parsed !== null) result.push(parsed);
                }
            }
            return result;
        }
    }
}
